import React, { useCallback, useEffect, useRef, useState } from 'react'
import useIsMounted from 'ismounted'
import PropTypes from 'prop-types'
import qHypercube from '../hooks/qHyperCube'
import { map } from 'lodash'

const QDocSimpleTable = ({
  qAppPromise,
  qDocPromise,
  cols,
  qPage,
  // style,
  ChartComponent,
  qInterColumnSortOrder,
  // height,
  // minRows,
  // showPageSizeOptions,
  // disableSelections,
}) => {
  const {
    qLayout,
    qData,
    changePage,
    select,
    applyPatches, //eslint-disable-line
  } = qHypercube({ qDocPromise, cols, qPage, qInterColumnSortOrder })
  const node = useRef(null)
  const isMounted = useIsMounted()

  /*
  const columns = useMemo(
    () =>
      qLayout
        ? [
            ...qLayout.qHyperCube.qDimensionInfo.map((col, index) => ({
              Header: col.qFallbackTitle,
              accessor: d => d[index].qText,
              colorIndex: col.qAttrExprInfo.findIndex(attr => attr.id === 'color'),
              defaultSortDesc: col.qSortIndicator === 'D',
              id: col.qFallbackTitle,
              qInterColumnIndex: index,
              qPath: `/qHyperCubeDef/qDimensions/${index}`,
              qSortIndicator: col.qSortIndicator,
              qReverseSort: col.qReverseSort,
              // getProps: (state, rowInfo) => {
              //   console.log(rowInfo);
              // },
            })),
            ...qLayout.qHyperCube.qMeasureInfo.map((col, index) => ({
              Header: col.qFallbackTitle,
              accessor: d => d[index + qLayout.qHyperCube.qDimensionInfo.length].qText,
              colorIndex: col.qAttrExprInfo.findIndex(attr => attr.id === 'color'),
              defaultSortDesc: col.qSortIndicator === 'D',
              id: col.qFallbackTitle,
              qInterColumnIndex: index + qLayout.qHyperCube.qDimensionInfo.length,
              qPath: `/qHyperCubeDef/qMeasures/${index}`,
              qSortIndicator: col.qSortIndicator,
              qReverseSort: col.qReverseSort,
              // getProps: (state, rowInfo) => {
              //   console.log(rowInfo);
              // },
            })),
          ]
        : [],
    [qLayout]
  )
*/

  // loading
  const [loading, setLoading] = useState(true) //eslint-disable-line
  useEffect(() => {
    if (qData && isMounted.current) {
      setLoading(false)
    }
  }, [qData, isMounted])

  // page size
  const [pageSize, setPageSize] = useState(qPage.qHeight)

  // page
  const [page, _setPage] = useState(0)
  const setPage = useCallback(
    _page => {
      if (isMounted.current) {
        setLoading(true)
        _setPage(_page)
        changePage({ qTop: _page * pageSize })
      }
    },
    [changePage, pageSize, isMounted]
  )

  window.setPage = setPage

  // pages
  const [pages, _setPages] = useState(0)
  const setPages = useCallback(
    _pages => {
      if (page >= _pages) {
        setPage(0)
      }
      _setPages(_pages)
    },
    [page, setPage]
  )

  useEffect(() => {
    if (!qLayout && isMounted.current) return
    setPages(Math.ceil(qLayout.qHyperCube.qSize.qcy / pageSize))
    setLoading(false)
  }, [qLayout, pageSize, setPage, setPages, isMounted])

  const handlePageChange = useCallback(
    pageIndex => {
      isMounted.current && setPage(pageIndex)
    },
    [setPage, isMounted]
  )

  const handleSortedChange = useCallback(
    async (newSorted, column) => {
      if (isMounted.current) {
        setLoading(true)
        // If no sort is set, we need to set a default sort order
        if (column.qSortIndicator === 'N') {
          if (column.qPath.includes('qDimensions')) {
            await applyPatches([
              {
                qOp: 'add',
                qPath: `${column.qPath}/qDef/qSortCriterias`,
                qValue: JSON.stringify([{ qSortByLoadOrder: 1 }]),
              },
            ])
          }
          if (column.qPath.includes('qMeasures')) {
            await applyPatches([
              {
                qOp: 'add',
                qPath: `${column.qPath}/qSortBy`,
                qValue: JSON.stringify({ qSortByLoadOrder: -1 }),
              },
            ])
          }
        }
        await applyPatches([
          {
            qOp: 'replace',
            qPath: `${column.qPath}/qDef/qReverseSort`,
            qValue: JSON.stringify((newSorted[0].desc !== column.defaultSortDesc) !== !!column.qReverseSort),
          },
          {
            qOp: 'replace',
            qPath: '/qHyperCubeDef/qInterColumnSortOrder',
            qValue: JSON.stringify(
              [...qLayout.qHyperCube.qEffectiveInterColumnSortOrder].sort((a, b) =>
                a === column.qInterColumnIndex ? -1 : b === column.qInterColumnIndex ? 1 : 0
              )
            ), //eslint-disable-line
          },
        ])
        setPage(0)
      }
    },
    [applyPatches, qLayout, setPage, isMounted]
  )

  const handlePageSizeChange = useCallback(
    _pageSize => {
      //eslint-disable-line
      if (isMounted.current) {
        setPageSize(_pageSize)
        changePage({ qHeight: _pageSize, qTop: page * _pageSize })
      }
    },
    [changePage, page, isMounted]
  )

  useEffect(() => {
    return () => {
      isMounted.current = false
    }
  }, [isMounted])

  const handleSelectMultiple = async (items, column = 0) => {
    const toSelect = map(items, item => {
      return item.qElemNumber
    })
    await select(column, toSelect)
  }

  const handleSelect = async (item, column = 0) => {
    const { qElemNumber } = item
    await select(column, [qElemNumber])
  }
  const handleFieldValueSelection = (field, values) => {
    qAppPromise.then(qApp => {
      qApp.field(field).selectValues(values, true, true)
    })
  }
  const handleClearAllSelection = field => {
    qAppPromise.then(qApp => {
      if (qApp) {
        if (!field) {
          qApp.selectionState().clearAll()
        } else {
          qApp.field(field).clear()
        }
      }
    })
  }

  return (
    <>
      {qData && isMounted.current && (
        <ChartComponent
          pages={pages}
          data={qData}
          handleSortChange={handleSortedChange}
          handleSelect={handleSelect}
          handlePageChange={handlePageChange}
          handlePageSizeChange={handlePageSizeChange}
          handleSelectMultiple={handleSelectMultiple}
          handleFieldSelect={handleFieldValueSelection}
          handleClearAllSelection={handleClearAllSelection}
          ref={node}
        />
      )}
    </>
  )
}

QDocSimpleTable.propTypes = {
  qAppPromise: PropTypes.object,
  qDocPromise: PropTypes.object.isRequired,
  cols: PropTypes.array,
  qPage: PropTypes.object,
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  ChartComponent: PropTypes.any,
  qInterColumnSortOrder: PropTypes.array,
  style: PropTypes.object,
  minRows: PropTypes.number,
  showPageSizeOptions: PropTypes.bool,
  disableSelections: PropTypes.bool,
}

QDocSimpleTable.defaultProps = {
  cols: null,
  qPage: {
    qTop: 0,
    qLeft: 0,
    qWidth: 20,
    qHeight: 40,
  },
  style: { height: '100%' },
  minRows: undefined,
  showPageSizeOptions: false,
  disableSelections: false,
}
export default QDocSimpleTable
